/**
 * Shared axios instance with global response error interceptor.
 *
 * All API files should import `apiClient` from here instead of the global `axios`.
 * The interceptor handles HTTP errors (401, 403, 5xx, network) automatically:
 * - Toasts a user-friendly message (unless the request sets `suppressErrorToast`)
 * - Reports to Sentry (unless the request sets `suppressErrorReport`)
 * - Marks the error as handled (`_interceptorHandled`) to prevent duplicate toasts in Oops
 * - Re-throws so component catch blocks still execute
 */

import axios from "axios";
import * as Sentry from "@sentry/browser";
import { Notifications } from "@infra/notifications";
import { setJwtToken, setRefreshToken, setLoginToken } from "./common";

export const apiClient = axios.create();

declare module "axios" {
	interface AxiosRequestConfig {
		/** The caller renders this request's failure itself (e.g. the optional weather overlay's quiet
		 *  notice), so the interceptor skips its toast. Session expiry still toasts and logs out. */
		suppressErrorToast?: boolean;
		/** The request's failures are expected, transient conditions the caller accounts for (e.g. the
		 *  weather grid's upstream stalls, which the backend deliberately keeps out of Sentry too), so
		 *  the interceptor skips its Sentry report. */
		suppressErrorReport?: boolean;
	}
}

const SERVER_ERROR_TOAST = "Server error. Please try again.";

/**
 * Tell the user and Sentry about a failure the server reported INSIDE a successful response: a
 * batch answer whose parts fail alone (`GET /jobs/issues/ids` names the queues it could not read).
 * Does what the interceptor below does for a 5xx — which is what each part was while it was a
 * request of its own — so folding requests into one does not make their failures silent.
 */
export function reportPartialServerError(error: Error) {
	Notifications.error(SERVER_ERROR_TOAST);
	Sentry.captureException(error, { mechanism: { handled: true } });
}

/** Callback invoked on auth failure (401) to clear React user state */
let authFailureHandler: (() => void) | null = null;

/**
 * Register a callback to be invoked when the interceptor detects a 401.
 * Call this from App.tsx to connect the interceptor to React state.
 */
export function setAuthFailureHandler(handler: () => void) {
	authFailureHandler = handler;
}

apiClient.interceptors.response.use(
	(response) => response,
	(error) => {
		const status = error.response?.status;
		const headers = error.config?.headers;
		const authorizationHeader =
			typeof headers?.get === "function"
				? headers.get("Authorization")
				: (headers?.Authorization ?? headers?.authorization);
		const hadAuthHeader = !!authorizationHeader;

		const requestUrl: string = error.config?.url ?? "";
		// An integration OAuth token (Proagrica) expired. The backend maps agX's
		// invalid_grant to 409 specifically so it does NOT trip the 401 logout below.
		// The import-form view model surfaces a "Reconnect" prompt; this is not an
		// app-session failure, so don't toast, log out, or report to Sentry.
		const isIntegrationReauth = status === 409 && requestUrl.includes("/refresh-token");
		const toastError = (message: string) => {
			if (!error.config?.suppressErrorToast) Notifications.error(message);
		};
		const reportError = () => {
			if (!error.config?.suppressErrorReport) Sentry.captureException(error, { mechanism: { handled: true } });
		};

		if (!error.response) {
			// No readable response reached the browser. Covers a true offline/DNS
			// failure AND the common case where the backend threw a 500 whose error
			// response never passed back through CORSMiddleware — so the browser strips
			// it and surfaces it as a "CORS policy" / net::ERR_FAILED network error with
			// no `error.response`. Either way the user needs to know the action failed.
			toastError("Couldn't reach the server. Please try again or contact software@rogoag.com.");
			reportError();
			error._interceptorHandled = true;
		} else if (isIntegrationReauth) {
			error._interceptorHandled = true;
		} else if (status === 401 && hadAuthHeader) {
			// Authenticated request got 401 → session expired
			setJwtToken("");
			setRefreshToken("");
			setLoginToken("");
			authFailureHandler?.();
			Notifications.error("Session expired. Please log in again.");
			error._interceptorHandled = true;
		} else if (status === 401 && !hadAuthHeader) {
			// Unauthenticated request (login/register) → let component handle
			// Don't toast, clear tokens, or set handled — Oops should process this
		} else if (status === 403) {
			toastError("Access denied.");
			reportError();
			error._interceptorHandled = true;
		} else if (status === 429) {
			toastError("Too many requests. Please wait a moment.");
			error._interceptorHandled = true;
		} else if (status && status >= 500) {
			toastError(SERVER_ERROR_TOAST);
			reportError();
			error._interceptorHandled = true;
		} else if (status && status >= 400) {
			// Other 4xx — report to Sentry only, don't toast or mark as handled.
			// Oops.handleError() still runs for business-specific messages (e.g., "User does not exist").
			reportError();
		}

		return Promise.reject(error);
	},
);
